import React, { useState } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES } from "../../utils/constants";
import { getScenarioNumByPath, getTimer } from "../../utils/helpers";
import { updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import { modalConfirm, modalQuestionResolution, modalHint, modalHintShow } from "../../utils/logger";
import { IconProgressHelp, IconRestore, IconHandMove, IconChevronLeft, IconChevronRight, IconZoomScan, IconX } from "@tabler/icons-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import CheckButton from "../../components/CheckButton";
import estasseguro from "../../assets/bg/estasseguro-avatar-violet.png";
import respuestacorrecta from "../../assets/bg/correcto-avatar.png";
import respuestaincorrecta from "../../assets/bg/incorrecto-avatar.png";
import btnPistaViolet from "../../assets/btnPistaViolet.png";
import imageHint from "../../assets/hints/imageHintBlue.png";
import imageHintUsed from "../../assets/btnPistaViolet.png";
import unIntento from "../../assets/un-intento-violet.png";
import dosIntentos from "../../assets/dos-intentos-violet.png";
import imgDesafio1 from "../../assets/img-caso/desafio-1.png";
import imgDesafio2 from "../../assets/img-caso/desafio-2.png";

function Scenario3Desafio({ intl }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const group = useSelector((state) => state.group);

  const activeScenario = getScenarioNumByPath(location.pathname);

  const QUIZ_ID = QUIZZES.DESAFIO;
  const SCENARIO_ID = SCENARIOS.SCENARIO_3;

  // ⚙️ Puntuación / intentos
  const NUMBERATTEMPTS = 2;
  const CORRECT_ANSWERS = ["Edad cronológica", "Frágiles", "Límite de edad"];
  const CORRECT_ANSWER_POINTS = 10;
  const WRONG_ANSWER_POINTS = -5;
  const CORRECT_ANSWER_POINTS_2 = 3;
  const WRONG_ANSWER_POINTS_2 = 0;

  // Estado drag & drop: 6 opciones únicas, 3 slots
  const items = [
    { id: "edad-cronologica", label: "Edad cronológica" },
    { id: "limite-edad", label: "Límite de edad" },
    { id: "fragiles", label: "Frágiles" },
    { id: "valoracion-biologica", label: "Valoración biológica" },
    { id: "aparentemente-sanos", label: "Aparentemente sanos" },
    { id: "limite-dosis", label: "Límite de dosis" },
  ];
  const [slots, setSlots] = useState([null, null, null]);

  // Modal visor de imágenes del desafío
  const desafioImages = [imgDesafio1, imgDesafio2];
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalIndex, setImageModalIndex] = useState(0);
  const openImageModal = (index) => {
    setImageModalIndex(index);
    setImageModalOpen(true);
  };
  const closeImageModal = () => setImageModalOpen(false);
  const goPrev = () => setImageModalIndex((i) => (i - 1 + desafioImages.length) % desafioImages.length);
  const goNext = () => setImageModalIndex((i) => (i + 1) % desafioImages.length);

  // Estado general
  const [isCompleted, setIsCompleted] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [hintPenalty, setHintPenalty] = useState(0);
  const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 2 intentos con envío único (suma final)
  const [attempt, setAttempt] = useState(1);            // 1..2
  const [pendingScore, setPendingScore] = useState(0);  // acumulado a enviar al final

  async function submitAnswer(finalScore, correctFinal) {
    const totalTime = getTimer();
    dispatch(updateTime(totalTime, 1));

    try {
      const res = await postAnswer({
        scenario: SCENARIO_ID,
        quiz: QUIZ_ID,
        group: group?._id,
        score: finalScore, // único envío con la suma final
        time: totalTime,
        lastVisited: ROUTES.SCENARIO3_FIN,
        correct: correctFinal,
      });

      if (res.status === 201) {
        setIsCompleted(true);
        navigate(ROUTES.SCENARIO3_FIN);
      }
    } catch (error) {
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      if (status === 409 && code === "E_DUPLICATE_ANSWER") {
        await modalQuestionResolution({
          correct: false,
          question: "Respuesta ya registrada",
          textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, pueden continuar con el resto del juego.</div>`,
          customConfirmButtonHtml: "Ir a la conclusión",
        });
        navigate(ROUTES.SCENARIO3_FIN);
        return;
      }

      await modalQuestionResolution({
        correct: false,
        question: "No se pudo guardar",
        textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
        customConfirmButtonHtml: "Aceptar",
      });

      setIsSubmitting(false);
    }
  }

  function resetValues() {
    setSlots([null, null, null]);
  }

  const useHint = async () => {
    const hint =
      'PENDIENTE PISTA';
    if (hintUsed) {
      await modalHintShow({ hint, imageHintUsed, color: "violet" });
      return;
    }
    const { used } = await modalHint({ image: imageHint, imageHintUsed, hint, color: "violet" });
    if (used && !hintUsed) {
      setHintUsed(true);
      setHintPenalty(3); // se aplicará una sola vez en el primer intento que compute puntos
    }
  };

  function checkAnswer() {
    if (slots.includes(null)) {
      modalQuestionResolution({
        correct: false,
        textHtml: `<img src=${estasseguro} alt="Debes completar los espacios" class="estasseguro" />
                   <div class="text-center txtTitulos mt-3 violet">Debes completar los tres espacios</div>`,
      });
      return;
    }

    const labelsInSlots = slots.map((id) => items.find((it) => it.id === id)?.label);
    const isCorrect =
      labelsInSlots[0] === CORRECT_ANSWERS[0] &&
      labelsInSlots[1] === CORRECT_ANSWERS[1] &&
      labelsInSlots[2] === CORRECT_ANSWERS[2];

    modalConfirm({ estasseguro, color: "violet" }).then((res) => {
      if (!res.isConfirmed) return;

      setIsSubmitting(true);
      const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;

      if (isCorrect) {
        // ✅ Correcto (1º o 2º intento)
        const base = attempt === 1 ? CORRECT_ANSWER_POINTS : CORRECT_ANSWER_POINTS_2;
        const attemptDelta = base - hintThisAttempt;
        const newScore = pendingScore + attemptDelta;
        if (hintThisAttempt) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: true,
          question: "El control con radioterapia en la próstata está recomendado en pacientes CPSCm...",
          textHtml: `<img src=${respuestacorrecta} alt="Respuesta correcta" style='max-width:420px' />
                     <div class="txt32 bold pt-4">Ganaron ${attemptDelta} puntos en este intento${hintThisAttempt ? " (-3 pista)" : ""}</div>
                     ${attempt === 2 ? `<div class="txt20 mt-2">Correcto en el segundo intento</div>` : ""}
                     <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Continuar",
          color: "violet",
        }).then(() => {
          submitAnswer(newScore, true); // envío único final
        });

        return;
      }

      // ❌ Incorrecto
      if (attempt < NUMBERATTEMPTS) {
        // 1er fallo: restamos -5 (y -3 si pista aquí). NO bloqueamos ni revelamos qué está mal.
        const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt; // -5 o -8 si pista aquí
        const newScore = pendingScore + attemptDelta;
        if (hintThisAttempt) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: false,
          question: "El control con radioterapia a la próstata está recomendado en pacientes CPSCm...",
          textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:420px' />
                     <div class="txt28 bold pt-3">La combinación no es correcta. ${attemptDelta} puntos${hintThisAttempt ? " (incluye -3 por pista)" : ""}</div>
                     <div class="txt22 mt-2">Les queda <b>1</b> intento. Revisa tu respuesta y vuelve a intentarlo.</div>
                     <div class="txt20 mt-2">Acumulado hasta ahora: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Intentar de nuevo",
          color: "violet",
        }).then(() => {
          setPendingScore(newScore);
          setAttempt(attempt + 1);
          // No bloqueamos ni reseteamos los slots (pueden ajustarlos manualmente).
          setIsSubmitting(false);
        });

        return;
      }

      // 2º fallo: delta 0 (o -3 si justo aquí aplica pista por 1ª vez). Enviamos.
      const attemptDelta = WRONG_ANSWER_POINTS_2 - hintThisAttempt; // 0 o -3
      const newScore = pendingScore + attemptDelta;
      if (hintThisAttempt) setHintAppliedToScore(true);

      modalQuestionResolution({
        correct: false,
        question: "El control con radioterapia a la próstata está recomendado en pacientes CPSCm...",
        textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:420px' />
                   <div class="txt32 bold pt-4">Han agotado los intentos</div>
                   ${hintThisAttempt ? `<div class="txt20 mt-2">Se aplicó -3 por pista en este intento</div>` : ""}
                   <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>`,
        customConfirmButtonHtml: "Continuar",
        color: "violet",
      }).then(() => {
        submitAnswer(newScore, false); // envío único final
      });
    });
  }

  // 🔄 Drag & Drop
  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;

    const newSlots = [...slots];

    // Pool -> Slot
    if (source.droppableId === "pool" && destination.droppableId.startsWith("slot")) {
      const slotIndex = parseInt(destination.droppableId.replace("slot-", ""));
      newSlots[slotIndex] = draggableId;
    }

    // Slot -> Pool
    if (source.droppableId.startsWith("slot") && destination.droppableId === "pool") {
      const slotIndex = parseInt(source.droppableId.replace("slot-", ""));
      newSlots[slotIndex] = null;
    }

    // Slot -> Slot
    if (source.droppableId.startsWith("slot") && destination.droppableId.startsWith("slot")) {
      const fromIndex = parseInt(source.droppableId.replace("slot-", ""));
      const toIndex = parseInt(destination.droppableId.replace("slot-", ""));
      newSlots[toIndex] = draggableId;
      newSlots[fromIndex] = null;
    }

    setSlots(newSlots);
  };

  const remainingAttempts = NUMBERATTEMPTS - attempt + 1;

  return (
    <div className="h-100">
      <div className="container-fluid h-100 px-5 d-flex flex-column justify-content-start pt-0">
        <div className="notaCasoClinicoAutor">caso clínico del autor</div>
        <DragDropContext onDragEnd={onDragEnd}>
          {/* Frase con slots */}
          <div className="row">
            <div className="col-12 recuadro recuadro-border-violet p-2">
              <h3 className="txt18 bold mb-0">
                Enunciado del desafío:
              </h3>
              <p className="txt18 mb-0">
                En el hospital donde se encuentran, los pacientes con mieloma en recaída triple expuestos serían elegibles para recibir tratamiento con anticuerpos monoclonales biespecíficos bien teclistamab o talquetamab.
              </p>
              <div className="d-flex gap-2 flex-wrap mt-2 align-items-start">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => openImageModal(0)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openImageModal(0); } }}
                  className="position-relative"
                  style={{ cursor: "pointer" }}
                  aria-label="Ver imagen 1 a tamaño completo"
                >
                  <img src={imgDesafio1} alt="Desafío 1" className="img-fluid" style={{ maxHeight: "125px" }} />
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => openImageModal(1)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openImageModal(1); } }}
                  className="position-relative d-inline-flex align-items-end"
                  style={{ cursor: "pointer" }}
                  aria-label="Ver imagen 2 a tamaño completo"
                >
                  <img src={imgDesafio2} alt="Desafío 2" className="img-fluid" style={{ maxHeight: "125px" }} />
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded ms-1"
                    style={{
                      backgroundColor: "#8c3888",
                      color: "white",
                      width: "48px",
                      height: "48px",
                      flexShrink: 0,
                    }}
                    title="Ver a tamaño completo"
                  >
                    <IconZoomScan size={40} stroke={2} />
                  </span>
                </div>
              </div>
              {imageModalOpen && (
                <div
                  className="position-fixed d-flex align-items-center justify-content-center p-3"
                  style={{
                    inset: 0,
                    zIndex: 9999,
                    backgroundColor: "rgba(0,0,0,0.85)",
                  }}
                  role="dialog"
                  aria-modal="true"
                  aria-label="Visor de imágenes"
                  onClick={closeImageModal}
                >
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); closeImageModal(); }}
                    className="position-absolute border-0 rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      top: "16px",
                      right: "16px",
                      width: "44px",
                      height: "44px",
                      backgroundColor: "#8c3888",
                      color: "white",
                      zIndex: 10001,
                    }}
                    aria-label="Cerrar"
                  >
                    <IconX size={28} stroke={2} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); goPrev(); }}
                    className="position-absolute border-0 rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      left: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: "48px",
                      height: "48px",
                      backgroundColor: "#8c3888",
                      color: "white",
                      zIndex: 10001,
                    }}
                    aria-label="Imagen anterior"
                  >
                    <IconChevronLeft size={32} stroke={2} />
                  </button>
                  <img
                    src={desafioImages[imageModalIndex]}
                    alt={`Desafío ${imageModalIndex + 1}`}
                    style={{
                      maxWidth: "90vw",
                      maxHeight: "90vh",
                      objectFit: "contain",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); goNext(); }}
                    className="position-absolute border-0 rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      right: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: "48px",
                      height: "48px",
                      backgroundColor: "#8c3888",
                      color: "white",
                      zIndex: 10001,
                    }}
                    aria-label="Siguiente imagen"
                  >
                    <IconChevronRight size={32} stroke={2} />
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <h3 className="txt18 bold mt-2">
                A partir de la evidencia anterior completen la siguiente frase arrastrando las palabras correspondientes:
              </h3>
              <p
                className="txt18"
                style={{
                  lineHeight: "1.3",
                  marginBottom: "5px",
                }}
              >
                La elegibilidad clínica no está restringida por la{" "}
                <Droppable droppableId="slot-0">
                  {(provided) => (
                    <span
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{
                        borderBottom: "2px dashed #999",
                        minWidth: "40px",
                        display: "inline-block",
                        margin: "0 4px",
                        minHeight: "30px",
                      }}
                    >
                      {slots[0] ? (
                        <Draggable draggableId={slots[0]} index={0}>
                          {(provided) => {
                            const item = items.find((it) => it.id === slots[0]);
                            return (
                              <span
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="px-2 py-1 rounded bgViolet text-white"
                              >
                                {item?.label}
                              </span>
                            );
                          }}
                        </Draggable>
                      ) : (
                        "_____"
                      )}
                      {provided.placeholder}
                    </span>
                  )}
                </Droppable>
                . La evidencia sugiere que los pacientes pueden recibir estos tratamientos sin importar su edad e inclusive aquellos categorizados como{" "}
                <Droppable droppableId="slot-1">
                  {(provided) => (
                    <span
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{
                        borderBottom: "2px dashed #999",
                        minWidth: "40px",
                        display: "inline-block",
                        margin: "0 4px",
                        minHeight: "30px",
                      }}
                    >
                      {slots[1] ? (
                        <Draggable draggableId={slots[1]} index={1}>
                          {(provided) => {
                            const item = items.find((it) => it.id === slots[1]);
                            return (
                              <span
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="px-2 py-1 rounded bgViolet text-white"
                              >
                                {item?.label}
                              </span>
                            );
                          }}
                        </Draggable>
                      ) : (
                        "_____"
                      )}
                      {provided.placeholder}
                    </span>
                  )}
                </Droppable>
                {" "}por escalas de la IMWG. Por tanto, no existe un{" "}
                <Droppable droppableId="slot-2">
                  {(provided) => (
                    <span
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{
                        borderBottom: "2px dashed #999",
                        minWidth: "40px",
                        display: "inline-block",
                        margin: "0 4px",
                        minHeight: "30px",
                      }}
                    >
                      {slots[2] ? (
                        <Draggable draggableId={slots[2]} index={2}>
                          {(provided) => {
                            const item = items.find((it) => it.id === slots[2]);
                            return (
                              <span
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="px-2 py-1 rounded bgViolet text-white"
                              >
                                {item?.label}
                              </span>
                            );
                          }}
                        </Draggable>
                      ) : (
                        "_____"
                      )}
                      {provided.placeholder}
                    </span>
                  )}
                </Droppable>
                {" "}absoluto para iniciar la terapia biespecífica si se cumple el perfil de recaída.
              </p>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              {/* Pool de palabras (solo las que NO estén en slots) */}
              <Droppable droppableId="pool" direction="horizontal">
                {(provided) => (
                  <div
                    className="position-relative d-flex justify-content-between mt-1"
                    style={{ backgroundColor: "#8c388844", borderRadius: "10px", padding: "5px"}}
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    <div className="d-flex align-items-start flex-wrap">
                      {items
                        .filter((it) => !slots.includes(it.id))
                        .map((item, index) => (
                          <Draggable key={item.id} draggableId={item.id} index={index}>
                            {(provided) => (
                              <div
                                className="mx-2 px-3 py-1 my-1 rounded shadow-sm txt16 bold cursor-pointer dragWidth text-center"
                                style={{ backgroundColor: "#8c3888", color: "white" }}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                {item.label}
                              </div>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </div>

                    <button
                      className="btn-main bold shadow bgViolet mt-1 me-1"
                      onClick={resetValues}
                      style={{ minHeight: "60px", minWidth: "60px" }}
                    >
                      <IconRestore size={30} color="white" stroke={2} />
                    </button>
                  </div>
                )}
              </Droppable>
            </div>
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between mt-1">
                <div className="d-flex justify-content-end mb-1">
                    <img src={remainingAttempts == 2 ? dosIntentos : unIntento} className="imgIntentos" />
                </div>
                <h5 className="mb-4 txt18 text-right my-2 violet">
                  <IconHandMove size={20} color="#8c3888" stroke={1} /> Mantengan presionado el valor unos instantes para moverlo al lugar correcto en la frase incompleta.<br/>
                  Si quieren cambiar la respuesta antes de validar, vuelvan a arrastrar el valor a su zona o cliquen el botón Reset <IconRestore size={20} color="#8c3888" stroke={2} />
                </h5>
              </div>
            </div>
          </div>
        </DragDropContext>

        <div className="d-flex justify-content-end mt-1">
          <CheckButton
            text="Comprobar respuesta"
            onClick={checkAnswer}
            disabled={isCompleted || isSubmitting}
            color="violet"
            loading={isSubmitting}
          />
        </div>
      </div>

      <ScenarioBar activeScenario={activeScenario} />
    </div>
  );
}

export default injectIntl(Scenario3Desafio);
